import React from 'react';
import { observer } from 'mobx-react';
import ChangeSettingsModal from './ChangeSettingsModal';
import CheckModelModal from './CheckModelModal';
import ExportModelModal from './ExportModelModal';
import ExportImageModalView from './ExportImageModal';
import ExportOETCsvModal from './ExportOETCsvModal';
import LearningReportModal from './LearningReportModal';
import SaveAsModal from './SaveAsModal';
import SaveVersionModal from './SaveVersionModal';
import RestoreVersionModal from './RestoreVersionModal';

import { useVisibleFSM } from '../../../hooks';
import { useStores } from '../../../hooks/useStores';
import type { IProject } from '../../../stores/projects/project';
import type { IModel } from '../../../stores/model';
import type { IOET } from '../../../stores/model/uimodel/oet';
import type { IMetaModel } from '../../../stores/model/metamodel';
import type { Notation } from '../../../diagram/ObjectTypeNode';

/** Props of the injecting {@link ExportImageModal} wrapper. */
interface ExportImageModalWrapperProps {
  /** Whether the modal is open. */
  isOpen: boolean;
  /** The MERODE model (used both to resolve the visible FSM and to export). */
  model: IModel;
  /** The model name used for the saved file. */
  modelName: string;
  /** The notation the EDG is exported in. */
  notation: Notation;
  /** The currently selected element (passed through, unused by the modal). */
  selected: string | null;
  /** Which diagram is being exported. */
  what?: 'edg' | 'oet' | 'fsm';
  /** Close the modal. */
  onRequestClose: () => void;
}

/**
 * Wrap ExportImageModal to inject the visible FSM. We need a functional
 * component to be able to use the useVisibleFSM custom hook.
 */
const ExportImageModal = ({ model, selected: _selected, ...rest }: ExportImageModalWrapperProps) => {
  const fsm = useVisibleFSM(model);
  return <ExportImageModalView {...rest} model={model} fsm={fsm} />;
};

/** Props of the {@link ProjectActions} container. */
interface ProjectActionsProps {
  /** The project whose action modals are rendered. */
  project: IProject;
}

/**
 * Container for the modals for the project actions.
 *
 * The store slices (selected/select/projectUI) were injected with mobx-react
 * (`inject(({ store }) => ({ selected: store.ui.selected, select: store.ui.select,
 * projectUI: store.ui.project }))`); they now come from useStores(). `project`
 * was never injected — it is passed down by the parent (Project/index.js).
 */
export default observer(function ProjectActions({ project }: ProjectActionsProps) {
  const store = useStores();
  const selected = store.ui.selected;
  const select = store.ui.select;
  const projectUI = store.ui.project;

  // Change settings

  const closeChangeSettingsModal = () => {
    projectUI.setChangeSettings(false);
  };

  const renderChangeModelSettingsModal = () => {
    return (
      <ChangeSettingsModal
        isOpen={projectUI.changeSettings}
        onRequestClose={closeChangeSettingsModal}
        onCancel={closeChangeSettingsModal}
        name={project.displayName}
        // Legacy behaviour: the settings/save modals open for a project that has
        // a model; assert non-null. The `IProject` interface types `model` as
        // nullable (a project *can* have none), but these modals never render
        // for a model-less project. Same assumption as IProject.lastEdited.
        model={project.model!}
      />
    );
  };

  // Check model

  const closeCheckModelModal = () => {
    projectUI.setCheckModel(false);
  };

  const renderCheckModelModal = () => {
    return (
      projectUI.checkModel && (
        <CheckModelModal
          isOpen={true}
          onRequestClose={closeCheckModelModal}
          modelVersion={project.activeModelVersion}
          fsm={projectUI.checkFSM}
        />
      )
    );
  };

  // Export model

  const closeExportModelModal = () => {
    projectUI.setExportModel(false);
  };

  const renderExportModelModal = () => {
    return (
      projectUI.exportModel && (
        <ExportModelModal
          isOpen={true}
          onRequestClose={closeExportModelModal}
          modelVersion={project.activeModelVersion}
          modelName={project.displayName}
        />
      )
    );
  };

  // Export image

  const closeExportImageModal = () => {
    projectUI.setExportEDGImage(false);
    projectUI.setExportOETImage(false);
    projectUI.setExportFSMImage(false);
  };

  const renderExportImageModal = () => {
    let what: 'edg' | 'oet' | 'fsm' | undefined;
    if (projectUI.exportEDGImage) {
      what = 'edg';
    } else if (projectUI.exportOETImage) {
      what = 'oet';
    } else if (projectUI.exportFSMImage) {
      what = 'fsm';
    }
    return (
      (projectUI.exportEDGImage || projectUI.exportOETImage || projectUI.exportFSMImage) && (
        <ExportImageModal
          isOpen={true}
          onRequestClose={closeExportImageModal}
          modelName={project.displayName}
          // Legacy behaviour: the export-image actions only fire for a loaded
          // model, so `model`/`activeVersion`/`content` are present here. The
          // `IProject` interface types them as nullable; assert non-null to keep
          // byte-identical runtime without adding guards. The MST `content` model
          // is structurally the typed `IModel` the modal wants.
          model={project.model!.activeVersion!.content!}
          notation={project.model!.notation}
          selected={selected}
          what={what}
        />
      )
    );
  };

  // Export OET csv

  const closeExportOETCsvModal = () => {
    projectUI.setExportOETCsv(false);
  };

  const renderExportOETCsvModal = () => {
    return (
      projectUI.exportOETCsv && (
        <ExportOETCsvModal
          isOpen={true}
          onRequestClose={closeExportOETCsvModal}
          modelName={project.displayName}
          // Legacy behaviour: the export-OET action only fires for a loaded
          // model, so `activeModelVersion`/`content` are present here; assert
          // non-null. The MST `oet` node is structurally the hand-declared `IOET`
          // (the interfaces use mutable arrays where MST views are readonly);
          // cast at this single boundary, same gap as stores/model/index.ts.
          oet={project.activeModelVersion!.content!.uimodel.oet as unknown as IOET}
        />
      )
    );
  };

  // Learning report

  const closeLearningReportModal = () => {
    projectUI.setLearningReport(false);
  };

  const renderLearningReportModal = () => {
    return (
      projectUI.learningReport && (
        <LearningReportModal
          isOpen={true}
          onRequestClose={closeLearningReportModal}
          modelName={project.displayName}
          // Legacy behaviour: the learning-report action only fires for a loaded
          // model, so `activeModelVersion`/`content` are present here; assert
          // non-null. The MST `metamodel` node is structurally the hand-declared
          // `IMetaModel` (mutable vs readonly view arrays); cast at this single
          // boundary, same gap as stores/model/index.ts.
          model={project.activeModelVersion!.content!.metamodel as unknown as IMetaModel}
        />
      )
    );
  };

  // Save as...

  const closeSaveAsModal = () => {
    projectUI.setSaveAs(false);
  };

  const renderSaveAsModal = () => {
    return (
      projectUI.saveAs && (
        <SaveAsModal isOpen={true} onRequestClose={closeSaveAsModal} model={project.model!} />
      )
    );
  };

  // Save version

  const closeSaveVersionModal = () => {
    projectUI.setSaveVersion(false);
  };

  const renderSaveVersionModal = () => {
    return (
      projectUI.saveVersion && (
        <SaveVersionModal
          isOpen={true}
          onRequestClose={closeSaveVersionModal}
          model={project.model!}
        />
      )
    );
  };

  // Restore version

  const closeRestoreVersionModal = () => {
    projectUI.setRestoreVersion(null);
  };

  const renderRestoreVersionModal = () => {
    return (
      projectUI.restoreVersion !== null && (
        <RestoreVersionModal
          isOpen={true}
          onRequestClose={closeRestoreVersionModal}
          model={project.model!}
          clearSelection={() => select(null)}
          id={projectUI.restoreVersion}
        />
      )
    );
  };

  return (
    <React.Fragment>
      {renderChangeModelSettingsModal()}
      {renderCheckModelModal()}
      {renderExportModelModal()}
      {renderExportImageModal()}
      {renderExportOETCsvModal()}
      {renderLearningReportModal()}
      {renderSaveAsModal()}
      {renderSaveVersionModal()}
      {renderRestoreVersionModal()}
    </React.Fragment>
  );
});
